import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserResponse } from "../../../../interfaces/user";
import { getAllUser, getUserMe } from "../../../../api/user.api";
import { getLocalStorage } from "../../../../api/axios";
import { UserAvatar } from "../../../../avatar/UserAvatar";

const ROLE_TONES: Record<string, string> = {
  admin:   "bg-violet-50 text-violet-700",
  barista: "bg-amber-50 text-amber-700",
  user:    "bg-ink-100 text-ink-700",
  dev:     "bg-emerald-50 text-emerald-700",
};

const TableUser: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [me, setMe] = useState<UserResponse | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [all, current] = await Promise.all([
        getAllUser(),
        getUserMe({ access_token: getLocalStorage("access_token").now_token ?? "" })
          .catch(() => null),
      ]);
      setUsers(all);
      setMe(current);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!term) return true;
      return (
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.role.toLowerCase().includes(term)
      );
    }).sort((a, b) => {
      // current user first, then alphabetical by name
      if (me && a.id === me.id) return -1;
      if (me && b.id === me.id) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [users, search, roleFilter, me]);

  // Reset to page 1 whenever the result set shrinks beneath the current page.
  useEffect(() => { setPage(1); }, [search, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const counts = useMemo(() => ({
    all:     users.length,
    user:    users.filter((u) => u.role === "user").length,
    barista: users.filter((u) => u.role === "barista").length,
    admin:   users.filter((u) => u.role === "admin").length,
    inactive: users.filter((u) => !u.isAvailable).length,
  }), [users]);

  return (
    <div className="min-h-screen bg-white font-sans pb-[40px]">
      <header className="px-5 pt-[24px] pb-[16px]">
        <p className="eyebrow-v2 mb-2">Admin</p>
        <h1 className="font-display font-black text-[28px] leading-[1.15] tracking-tightest text-ink-1000">
          User management
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          {counts.all} total · {counts.user} users · {counts.barista} baristas · {counts.admin} admins
          {counts.inactive > 0 && <> · <span className="text-red-700">{counts.inactive} inactive</span></>}
        </p>
      </header>

      {/* Search + role filter */}
      <div className="px-5 py-3 border-b border-ink-100 space-y-3">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, role…"
            className="w-full h-[44px] pl-10 pr-4 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-sm text-ink-1000 placeholder:text-ink-400"
          />
          <svg className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: "all",     label: `All (${counts.all})` },
            { id: "user",    label: `Users (${counts.user})` },
            { id: "barista", label: `Baristas (${counts.barista})` },
            { id: "admin",   label: `Admins (${counts.admin})` },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setRoleFilter(f.id)}
              className={f.id === roleFilter ? "chip-v2-active whitespace-nowrap" : "chip-v2 whitespace-nowrap"}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <main className="px-5 pt-[8px]">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="row-v2 animate-pulse">
              <div className="w-[44px] h-[44px] rounded-full bg-ink-100" />
              <div className="flex-1 space-y-2">
                <div className="h-[14px] bg-ink-200 rounded w-2/3" />
                <div className="h-3 bg-ink-100 rounded w-1/2" />
              </div>
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="py-[64px] flex flex-col items-center justify-center text-center">
            <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="font-display font-black text-lg text-ink-1000">No users match</p>
            <button onClick={() => { setSearch(""); setRoleFilter("all"); }} className="text-sm text-ink-700 underline mt-2">Clear filters</button>
          </div>
        ) : (
          paginated.map((u) => {
            const isMe = me && u.id === me.id;
            const roleTone = ROLE_TONES[u.role] ?? ROLE_TONES.user;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => navigate(`/admin/user/${u.id}/edit`)}
                className="row-v2 w-full text-left active:bg-ink-50"
              >
                <UserAvatar avatar={u.avatar} name={u.name} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-base text-ink-1000 truncate">{u.name}</span>
                    {isMe && (
                      <span className="inline-flex items-center px-1.5 h-[16px] rounded-full bg-ink-1000 text-ink-0 text-[9px] font-bold uppercase tracking-wider">
                        You
                      </span>
                    )}
                    {!u.isAvailable && (
                      <span className="inline-flex items-center px-1.5 h-[16px] rounded-full bg-red-50 text-red-700 text-[9px] font-bold uppercase tracking-wider">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ink-500 truncate">{u.email}</p>
                </div>
                <span className={`inline-flex items-center px-2.5 h-[22px] rounded-full text-[10px] font-bold uppercase tracking-wider ${roleTone}`}>
                  {u.role}
                </span>
                <svg className="w-4 h-4 text-ink-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            );
          })
        )}

        {/* Pagination */}
        {!loading && filtered.length > PAGE_SIZE && (
          <nav className="flex items-center justify-between gap-3 pt-[20px] pb-[8px]" aria-label="Pagination">
            <p className="text-xs text-ink-500 font-medium">
              {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="w-9 h-9 rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100 disabled:opacity-30"
                aria-label="Previous page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              {pageNumbers(safePage, totalPages).map((p, i) =>
                p === "…" ? (
                  <span key={`g${i}`} className="px-2 text-ink-400 text-xs">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 rounded-full text-xs font-bold tabular-nums ${
                      p === safePage
                        ? "bg-ink-1000 text-ink-0"
                        : "text-ink-700 hover:bg-ink-100"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="w-9 h-9 rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100 disabled:opacity-30"
                aria-label="Next page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </nav>
        )}
      </main>
    </div>
  );
};

/** 1, 2, 3, …, last — collapses long ranges with ellipses. */
function pageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("…");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
}

export default TableUser;
